import ActionDialog from "@dashboard/components/ActionDialog";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import {
  useWsmProductComplianceDeleteMutation,
  useWsmProductComplianceDetailsQuery,
  useWsmProductComplianceUpdateMutation,
} from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import useNotifier from "@dashboard/hooks/useNotifier";
import { commonMessages } from "@dashboard/intl";
import { getMutationState } from "@dashboard/misc";
import { Skeleton } from "@saleor/macaw-ui-next";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import WsmCompliancePage from "../components/WsmCompliancePage/WsmCompliancePage";
import { sectionMessages } from "../messages";
import { wsmComplianceListPath } from "../urls";
import {
  complianceToFormData,
  emptyComplianceFormData,
  getComplianceInput,
} from "../utils";

/**
 * Keyed by the PRODUCT, not by the row: the record is a OneToOne on it and the
 * contract has no create mutation, only an upsert. A product with no record yet
 * opens the same card, empty.
 */
export const WsmComplianceDetailsView = ({ productId }: { productId: string }) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const notify = useNotifier();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { data, loading, refetch } = useWsmProductComplianceDetailsQuery({
    variables: { product: productId },
  });
  const [updateCompliance, updateComplianceOpts] = useWsmProductComplianceUpdateMutation({
    onCompleted: async result => {
      if (!result.wsmProductComplianceUpdate?.errors.length) {
        notify({ status: "success", text: intl.formatMessage(commonMessages.savedChanges) });
        // The first save CREATES the row, so the card has to pick up its new id.
        await refetch();
      }
    },
  });
  const [deleteCompliance, deleteComplianceOpts] = useWsmProductComplianceDeleteMutation({
    onCompleted: result => {
      if (!result.wsmProductComplianceDelete?.errors.length) {
        notify({ status: "success", text: intl.formatMessage(commonMessages.savedChanges) });
        navigate(wsmComplianceListPath);
      } else {
        setDeleteOpen(false);
      }
    },
  });
  const compliance = data?.wsmProductCompliance;
  const errors = updateComplianceOpts.data?.wsmProductComplianceUpdate?.errors ?? [];

  if (loading) {
    return (
      <DetailPageLayout>
        <TopNav
          href={wsmComplianceListPath}
          title={intl.formatMessage(sectionMessages.complianceListTitle)}
        />
        <DetailPageLayout.Content>
          <Skeleton />
        </DetailPageLayout.Content>
      </DetailPageLayout>
    );
  }

  // ponytail: a product with no record yet has no name to show, because the only
  // query that carries one is the record itself. The ceiling is a plain title on
  // the first visit; the upgrade is the stock product query, one more round trip,
  // and it is not worth one until a merchant asks for it.
  const title = compliance
    ? intl.formatMessage(sectionMessages.complianceTitle, { name: compliance.product.name })
    : intl.formatMessage(sectionMessages.complianceListTitle);

  return (
    <>
      <WsmCompliancePage
        key={compliance?.id ?? productId}
        title={title}
        productName={compliance?.product.name}
        knownZones={compliance?.includeShippingZones ?? []}
        initialData={
          compliance ? complianceToFormData(compliance) : emptyComplianceFormData()
        }
        errors={errors}
        disabled={updateComplianceOpts.loading}
        saveButtonBarState={getMutationState(
          updateComplianceOpts.called,
          updateComplianceOpts.loading,
          errors,
        )}
        onSubmit={formData =>
          updateCompliance({
            variables: { product: productId, input: getComplianceInput(formData) },
          })
        }
        onDelete={compliance ? () => setDeleteOpen(true) : undefined}
      />
      <ActionDialog
        open={deleteOpen}
        variant="delete"
        title={intl.formatMessage(sectionMessages.complianceDeleteTitle)}
        confirmButtonState={getMutationState(
          deleteComplianceOpts.called,
          deleteComplianceOpts.loading,
          [],
        )}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() =>
          compliance && deleteCompliance({ variables: { id: compliance.id } })
        }
      >
        {intl.formatMessage(sectionMessages.complianceDeleteContent, {
          name: compliance?.product.name ?? "",
        })}
      </ActionDialog>
    </>
  );
};

export default WsmComplianceDetailsView;
